import { Logger, ILogObj, ISettingsParam } from "tslog";

// Map app-level log names to tslog numeric levels
// tslog levels: 0: silly, 1: trace, 2: debug, 3: info, 4: warn, 5: error, 6: fatal
type LogLevel = "DEBUG" | "INFO" | "WARN" | "ERROR";

// tslog uses numeric levels
const APP_TO_TSLOG_LEVEL: Record<LogLevel, number> = {
  DEBUG: 2, // tslog debug level
  INFO: 3, // tslog info level
  WARN: 4, // tslog warn level
  ERROR: 5, // tslog error level
};

/**
 * Determine minimum log level based on environment
 * @returns tslog numeric level (0-6)
 */
function getMinLevel(): number {
  const override = import.meta.env.VITE_LOG_LEVEL?.toUpperCase() as LogLevel;
  if (override && override in APP_TO_TSLOG_LEVEL) {
    return APP_TO_TSLOG_LEVEL[override];
  }

  if (import.meta.env.DEV || import.meta.env.VITE_ENVIRONMENT === "dev") {
    return 2; // Everything debug and above
  }
  if (import.meta.env.VITE_ENVIRONMENT === "stg") {
    return 4; // Warnings and errors only
  }
  return 5; // Otherwise, only show errors (e.g., prod)
}

/**
 * Get tslog settings based on environment
 */
function getSettings(): ISettingsParam<ILogObj> {
  const env = import.meta.env.VITE_ENVIRONMENT;
  const isProd = env === "prod" || import.meta.env.PROD;

  return {
    name: "web", // Root logger name
    minLevel: getMinLevel(), // tslog filters below this level
    type: isProd ? "json" : "pretty", // pretty for dev, json for prod
    hideLogPositionForProduction: true, // Performance (see https://tslog.js.org/#/?id=%e2%9d%97performance)
  };
}

// Create root logger for the entire app (singleton)
const rootLogger: Logger<ILogObj> = new Logger<ILogObj>(getSettings());

/**
 * Factory function to create module-specific sub-loggers
 * Sub-loggers inherit settings from root and add module name prefix
 *
 * @param moduleName - Name of the module/component (e.g., "OAuthCallback", "Reader")
 * @returns tslog sub-logger instance
 */
export function createLogger(moduleName: string): Logger<ILogObj> {
  return rootLogger.getSubLogger({ name: moduleName });
}

// Export rootLogger for advanced use cases (e.g., attaching transports)
export { rootLogger };
